// A poll in the feed (PLAN-2026-10-30-polls-in-chat §4): its question, then
// each option as a row that is a button, its bar filled to its share and its
// count at the right, a check on mine. A tap votes, moves the vote, or on my
// own takes it back; a closed poll's rows are not buttons. With the message's
// bar open, who voted for each is said under it (UX-8), as reactions are.

import { useEffect, useState } from 'react';
import { whenInZone, type ChatMessage } from '@fsp/domain';
import { deviceZone, useReaderZone } from '../data/hooks';
import { Icon } from '../kit/Icon';
import { Meter } from '../kit/ui';

type Poll = NonNullable<ChatMessage['poll']>;

/** What won a closed poll, or the tie: *Pizza won, 5 of 7*. */
function result(poll: Poll): string {
  if (poll.total === 0) return 'Closed · nobody voted';
  const top = Math.max(...poll.options.map((o) => o.votes));
  const winners = poll.options.filter((o) => o.votes === top);
  return winners.length === 1
    ? `Closed · ${winners[0]!.text} won, ${top} of ${poll.total}`
    : `Closed · a tie, ${top} each`;
}

export function PollCard({
  poll,
  open,
  onVote,
}: {
  poll: Poll;
  /** The message's bar is open: say who voted for each. */
  open: boolean;
  /** Absent where nobody can vote (a hidden poll, a feed that takes none). */
  onVote?: (option: number | null) => void;
}) {
  const zone = useReaderZone() ?? deviceZone();
  // CLOSED AT ITS TIME ON THIS SCREEN TOO: the close is no change to the
  // message, so no answer brings it, and a feed left open would keep a poll
  // live past its close.
  const closesAt = Date.parse(poll.closesAt);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const wait = closesAt - Date.now();
    if (poll.closed || wait <= 0 || wait > 2 ** 31 - 1) return;
    const t = window.setTimeout(() => setNow(Date.now()), wait + 250);
    return () => window.clearTimeout(t);
  }, [closesAt, poll.closed]);
  const closed = poll.closed || closesAt <= now;
  const live = !closed && onVote !== undefined;
  return (
    <div className="fsp-poll" data-closed={closed || undefined}>
      <div className="fsp-poll-question">
        <Icon name="poll" size="sm" />
        <span>{poll.question}</span>
      </div>
      <div className="fsp-poll-options" role="group" aria-label={poll.question}>
        {poll.options.map((o, i) => {
          const share = poll.total === 0 ? 0 : Math.round((o.votes / poll.total) * 100);
          const said = `${o.text}, ${o.votes} ${o.votes === 1 ? 'vote' : 'votes'}${o.mine ? ', your vote' : ''}`;
          const row = (
            <>
              <span className="fsp-poll-bar" aria-hidden>
                <Meter value={share / 100} />
              </span>
              <span className="fsp-poll-text">
                {o.mine ? <Icon name="accept" size="xs" /> : null}
                {o.text}
              </span>
              <span className="fsp-poll-count fsp-num">
                {o.votes}
                {poll.total > 0 ? ` · ${share}%` : ''}
              </span>
            </>
          );
          return (
            <div key={i} className="fsp-poll-option">
              {live ? (
                <button
                  type="button"
                  className="fsp-poll-row"
                  aria-pressed={o.mine}
                  aria-label={o.mine ? `${said}. Take it back.` : `${said}. Vote for it.`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onVote(o.mine ? null : i);
                  }}
                >
                  {row}
                </button>
              ) : (
                <div className="fsp-poll-row" aria-label={said}>
                  {row}
                </div>
              )}
              {open && o.voters.length > 0 ? (
                <p className="fsp-poll-voters">{o.voters.join(', ')}</p>
              ) : null}
            </div>
          );
        })}
      </div>
      <p className="fsp-poll-foot">
        {closed
          ? result(poll)
          : `${poll.total} ${poll.total === 1 ? 'vote' : 'votes'} · closes ${whenInZone(poll.closesAt, zone, Date.now())}`}
      </p>
    </div>
  );
}
